import config from '@/config';
import fetchWrapper from '@/services/fetchWrapper.service';

const SUPPORT_CONFIG_CACHE_TTL_MS = 5 * 60 * 1000;
const supportConfigCache = new Map();
const supportInFlightRequests = new Map();

function getConfiguration(userId, { force = false } = {}) {
  if (!userId) {
    return Promise.resolve({ response: null, data: null });
  }

  const cacheKey = String(userId);

  if (!force) {
    const cached = supportConfigCache.get(cacheKey);
    if (cached && cached.expiresAt > Date.now()) {
      return Promise.resolve(cached.payload);
    }

    if (supportInFlightRequests.has(cacheKey)) {
      return supportInFlightRequests.get(cacheKey);
    }
  }

  const request = (async () => {
    const result = await fetchWrapper.get(`${config.apis.api.url}/support/${userId}`, {
      injectToken: true,
    });

    if (result?.response?.ok) {
      supportConfigCache.set(cacheKey, {
        payload: result,
        expiresAt: Date.now() + SUPPORT_CONFIG_CACHE_TTL_MS,
      });
    } else {
      supportConfigCache.delete(cacheKey);
    }

    return result;
  })();

  supportInFlightRequests.set(cacheKey, request);

  request.finally(() => {
    if (supportInFlightRequests.get(cacheKey) === request) {
      supportInFlightRequests.delete(cacheKey);
    }
  });

  return request;
}

function upsertConfiguration(userId, payload) {
  if (!userId) {
    return Promise.resolve({ response: null, data: null });
  }

  const currencyField = ['USD', 'EUR', 'GBP'];

  return fetchWrapper.put(`${config.apis.api.url}/upsert/support/${userId}`, {
    injectToken: true,
    body: {
      ...payload,
      currency: currencyField,
    },
  }).then((result) => {
    if (result?.response?.ok) {
      const cacheKey = String(userId);
      supportConfigCache.delete(cacheKey);
      supportInFlightRequests.delete(cacheKey);
    }
    return result;
  });
}

function createPaymentIntent(supportedUserId, payload) {
  if (!supportedUserId) {
    return Promise.resolve({ response: null, data: null });
  }

  return fetchWrapper.post(`${config.apis.api.url}/support/${supportedUserId}/payment-intent`, {
    injectToken: true,
    body: payload,
  });
}
 
function finalizeContribution(supportedUserId, payload) {
  if (!supportedUserId) {
    return Promise.resolve({ response: null, data: null });
  }

  return fetchWrapper.post(`${config.apis.api.url}/support/${supportedUserId}/contributions`, {
    injectToken: true,
    body: payload,
  });
}

function calculateVat(supportedUserId, { amount, paymentMethodId, buyerCountry, forceNonBusinessPurchase } = {}) {
  if (!supportedUserId) {
    return Promise.resolve({ response: null, data: null });
  }

  const params = new URLSearchParams();
  if (typeof amount === 'number' && Number.isFinite(amount)) {
    params.set('amount', amount);
  }
  if (paymentMethodId) {
    params.set('payment_method_id', paymentMethodId);
  }
  if (buyerCountry) {
    params.set('buyer_country', buyerCountry);
  }
  if (forceNonBusinessPurchase === true) {
    params.set('force_non_business_purchase', 'true');
  }
  const query = params.toString() ? `?${params.toString()}` : '';
  return fetchWrapper.get(`${config.apis.api.url}/support/${supportedUserId}/vat${query}`, {
    injectToken: true,
  });
}

function cancelSupportSubscription(subscriptionId) {
  if (!subscriptionId) {
    return Promise.resolve({ response: null, data: null });
  }

  return fetchWrapper.put(`${config.apis.api.url}/support/subscriptions/${subscriptionId}/cancel`, {
    injectToken: true,
  });
}

function resumeSupportSubscription(subscriptionId) {
  if (!subscriptionId) {
    return Promise.resolve({ response: null, data: null });
  }

  return fetchWrapper.put(`${config.apis.api.url}/support/subscriptions/${subscriptionId}/resume`, {
    injectToken: true,
  });
}

function switchSupportSubscriptionBusinessBilling(subscriptionId, { setup_intent_id, payment_method_id } = {}) {
  if (!subscriptionId) {
    return Promise.resolve({ response: null, data: null });
  }

  return fetchWrapper.put(`${config.apis.api.url}/support/subscriptions/${subscriptionId}/switch-business-billing`, {
    injectToken: true,
    body: {
      setup_intent_id: setup_intent_id ?? null,
      payment_method_id: payment_method_id ?? null,
    },
  });
}

const SupportService = {
  getConfiguration,
  upsertConfiguration,
  createPaymentIntent,
  finalizeContribution,
  calculateVat,
  cancelSupportSubscription,
  resumeSupportSubscription,
  switchSupportSubscriptionBusinessBilling,
};

export default SupportService;
